import { z } from "zod";
import type { RecurringGroup } from "./recurring.js";

/**
 * Triage and the cancellation playbook: what the user decided about each
 * recurring charge, what Claude suggested, and the card that tells them how
 * to cancel it.
 */

export const Decision = z.enum(["keep", "cancel", "unsure"]);
export type Decision = z.infer<typeof Decision>;

export const SetDecisionRequest = z
  .object({
    key: z.string().min(1).max(300),
    /** null clears the decision and puts the charge back in the undecided queue. */
    decision: Decision.nullable(),
  })
  .strict();
export type SetDecisionRequest = z.infer<typeof SetDecisionRequest>;

export const CardRequest = z.object({ key: z.string().min(1).max(300) }).strict();

export const BilledBy = z.enum(["direct", "apple", "google", "paypal", "amazon", "other"]);
export type BilledBy = z.infer<typeof BilledBy>;

/** Claude's read on one recurring charge. Advisory only; the user decides. */
export const Suggestion = z.object({
  key: z.string(),
  /** The service as a person would name it, e.g. "HBO Max". */
  service: z.string(),
  billedBy: BilledBy,
  category: z.string().describe("Short category such as streaming, software, telecom, utilities, insurance, news"),
  suggestion: z.enum(["keep", "cancel", "unsure"]),
  /** One line, e.g. "You pay for 5 streaming services". */
  reason: z.string(),
  /** Keys of other charges that do the same job, for the overlap badge. */
  overlapsWith: z.array(z.string()),
});
export type Suggestion = z.infer<typeof Suggestion>;

export const CancelRoute = z.enum(["web_toggle", "billing_platform", "email", "phone", "card_block"]);
export type CancelRoute = z.infer<typeof CancelRoute>;

/**
 * How to cancel one service. Cached per merchant key, so it never holds
 * anything personal: the copy uses the placeholders below, which the web app
 * fills in from the charge history.
 */
export const CancellationCard = z.object({
  service: z.string(),
  route: CancelRoute,
  /** 1 to 5 steps, each a single action. */
  steps: z.array(z.string()),
  /** Where the cancel button lives, or null when there is no web route. */
  url: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  /** Paste-ready message when a human has to be involved, else null. */
  emailSubject: z.string().nullable(),
  emailBody: z.string().nullable(),
  confidence: z.enum(["high", "medium", "low"]),
  /** The page the steps came from. */
  sourceUrl: z.string().nullable(),
  /** Anything worth knowing, e.g. a retention offer or a notice period. */
  note: z.string().nullable(),
});
export type CancellationCard = z.infer<typeof CancellationCard>;

/** Placeholders a card's copy may use; filled in by the web app. */
export const CARD_PLACEHOLDERS = {
  lastChargeDate: "{{LAST_CHARGE_DATE}}",
  lastChargeAmount: "{{LAST_CHARGE_AMOUNT}}",
  accountEmail: "{{ACCOUNT_EMAIL}}",
} as const;

export function fillPlaceholders(text: string, values: { lastChargeDate: string; lastChargeAmount: string; accountEmail?: string }): string {
  return text
    .replaceAll(CARD_PLACEHOLDERS.lastChargeDate, values.lastChargeDate)
    .replaceAll(CARD_PLACEHOLDERS.lastChargeAmount, values.lastChargeAmount)
    .replaceAll(CARD_PLACEHOLDERS.accountEmail, values.accountEmail || "[your account email]");
}

export interface LinkCheck {
  ok: boolean;
  status?: number;
  checkedAt: string;
  error?: string;
}

export interface CardRecord {
  status: "pending" | "ready" | "failed";
  card?: CancellationCard;
  linkCheck?: LinkCheck;
  error?: string;
  updatedAt: string;
}

export type JobStatus = "idle" | "running" | "done" | "failed";

/** One row of the triage screen. */
export interface TriageItem extends RecurringGroup {
  decision: Decision | null;
  suggestion?: Suggestion;
  card?: CardRecord;
}

export interface RecurringResponse {
  groups: TriageItem[];
  statementCount: number;
  chargeCount: number;
  /** Statements still waiting on the user; their rows are not counted yet. */
  pendingReviewCount: number;
  suggest: { status: JobStatus; updatedAt?: string; error?: string };
}

export interface TriageTotals {
  keptCents: number;
  cancelCents: number;
  unsureCents: number;
  undecidedCents: number;
}

/** Yearly totals per pane. Charges that stopped are left out: they cost nothing going forward. */
export function triageTotals(items: Array<Pick<TriageItem, "decision" | "yearlyCostCents" | "flags">>): TriageTotals {
  const t: TriageTotals = { keptCents: 0, cancelCents: 0, unsureCents: 0, undecidedCents: 0 };
  for (const i of items) {
    if (i.flags.includes("stopped")) continue;
    if (i.decision === "keep") t.keptCents += i.yearlyCostCents;
    else if (i.decision === "cancel") t.cancelCents += i.yearlyCostCents;
    else if (i.decision === "unsure") t.unsureCents += i.yearlyCostCents;
    else t.undecidedCents += i.yearlyCostCents;
  }
  return t;
}

/** The next expected charge date, from the cadence and the last charge. */
export function nextChargeDate(group: Pick<RecurringGroup, "cadence" | "lastSeen">): string | undefined {
  const days = { weekly: 7, monthly: 30.44, quarterly: 91.31, annual: 365.25 }[group.cadence as "weekly"];
  if (!days) return undefined;
  const t = Date.parse(group.lastSeen) + Math.round(days) * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}
